/**
 * `GET /api/reference/**` — the game's read-only rulebook (worlds, classes, tasks). Long
 * `staleTime` since this only changes when an admin re-seeds; see `ReferenceController`.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from './api';

export interface WorldDto {
  slug: string;
  name: string;
  worldType: string;
  region: string;
  active: boolean;
  mergedInto: string | null;
  verified: boolean;
}

export interface LinkSkillDto {
  name: string;
  effectMax: string;
  maxLevel: number;
  sharedGroup: string | null;
}

export interface LegionEffectDto {
  effectText: string;
}

export interface ClassDto {
  slug: string;
  name: string;
  branch: string;
  jobType: string;
  // REV8: some unverified early-seed classes (e.g. Cygnus Knights) have no mainStat yet — was
  // typed as non-nullable `string` before REV8 needed to distinguish "has a mainStat" from not
  // for the generic legion-effect fallback text (lib/legion.ts's legionEffectText).
  mainStat: string | null;
  sixthJobSystem: string;
  releasedGms: string | null;
  verified: boolean;
  linkSkill: LinkSkillDto | null;
  legionEffect: LegionEffectDto | null;
}

export interface BossDifficultyDto {
  difficulty: string;
  levelReq: number;
  forceType: string;
  forceReq: number | null;
  partyMax: number | null;
  crystalValue: number | null;
  crystalType: string | null;
  notableDrops: unknown;
  timeEstimateMin: string | null;
  verified: boolean;
}

export interface BossDto {
  slug: string;
  name: string;
  tier: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  sharedClearGroup: string;
  difficulties: BossDifficultyDto[];
}

export interface TaskDto {
  slug: string;
  name: string;
  category: string;
  cadence: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  scope: 'CHARACTER' | 'ACCOUNT' | 'WORLD';
  maxCount: number;
  levelReq: number | null;
  requirements: unknown;
  rewardSummary: string | null;
  timeEstimateMin: string | null;
  gmsOnly: boolean;
  verified: boolean;
}

export interface SymbolDailySourceDto {
  amount: number;
  conditionText: string | null;
  verified: boolean;
}

/** {@code level} is always 0 — the "cumulative total to max level" sentinel (see V3 migration). */
export interface SymbolLevelCostDto {
  level: number;
  symbolsRequired: number;
  mesoCost: number | null;
  verified: boolean;
}

export interface SymbolDto {
  slug: string;
  name: string;
  type: 'ARCANE' | 'SACRED' | 'GRAND_SACRED';
  region: string;
  levelReq: number;
  maxLevel: number;
  verified: boolean;
  dailySource: SymbolDailySourceDto | null;
  /** Every `symbol_level_cost` row for this symbol's type — see `lib/symbols.ts` for why this is a list. */
  levelCosts: SymbolLevelCostDto[];
}

export interface StarforceRateDto {
  star: number;
  successPct: number;
  failPct: number;
  destroyPct: number;
  verified: boolean;
}

export interface StarforceCostParamDto {
  starBand: string;
  divisor: number;
  verified: boolean;
}

export interface BenchmarkTemplateDto {
  slug: string;
  name: string;
  description: string | null;
  /** Account-wide, not per-slot (WP3 note): `{uniformTargetStar}` or a `*Range` pair. */
  slots: {
    uniformTargetStar?: number;
    uniformTargetStarRange?: [number, number];
    stretchTargetStarRange?: [number, number];
    note?: string;
  };
  verified: boolean;
}

export interface StarforceBundleDto {
  rates: StarforceRateDto[];
  costParams: StarforceCostParamDto[];
  benchmarkTemplates: BenchmarkTemplateDto[];
}

/** REV6 (docs/specs/REV6-pitched-tracker.md) — one Pitched or Brilliant Boss Set item. */
export interface PitchedItemDto {
  slug: string;
  name: string;
  setKey: 'PITCHED' | 'BRILLIANT';
  equipSlot: string;
  sourceBossSlug: string;
  sourceDifficulties: string[];
  boxObtainable: boolean;
  retired: boolean;
  verified: boolean;
}

/**
 * REV21 (docs/specs/REV21-hexa-skill-icons.md): one class's HEXA node -> real skill catalog row
 * (research 11). `nodeType` is never `common_core` (see `GoalsService`). Multiple rows can share a
 * `(nodeType, nodeIndex)` pair for a Mastery slot — REV22 Fix 3 (docs/specs/REV22-hexa-stat-and-
 * merged-masteries.md) found this isn't player choice: a Mastery Core upgrades ALL its listed
 * skills at once. `GoalsDtos.HexaNodeDto#skills` is the ordinal-ordered merged view of this same
 * data for a specific node; this catalog type is still used directly for the uncataloged-class
 * "add/remove a flexible node" fallback flow.
 */
export interface HexaClassSkillDto {
  classSlug: string;
  nodeType: string;
  nodeIndex: number;
  skillSlug: string;
  skillName: string;
  verified: boolean;
}

const REFERENCE_STALE_TIME = 60 * 60 * 1000;

export const worldsQuery = {
  queryKey: ['reference', 'worlds'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<WorldDto[]>('/api/reference/worlds', signal),
  staleTime: REFERENCE_STALE_TIME,
};

export const classesQuery = {
  queryKey: ['reference', 'classes'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<ClassDto[]>('/api/reference/classes', signal),
  staleTime: REFERENCE_STALE_TIME,
};

export const tasksQuery = {
  queryKey: ['reference', 'tasks'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<TaskDto[]>('/api/reference/tasks', signal),
  staleTime: REFERENCE_STALE_TIME,
};

export const bossesQuery = {
  queryKey: ['reference', 'bosses'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<BossDto[]>('/api/reference/bosses', signal),
  staleTime: REFERENCE_STALE_TIME,
};

export const symbolsQuery = {
  queryKey: ['reference', 'symbols'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<SymbolDto[]>('/api/reference/symbols', signal),
  staleTime: REFERENCE_STALE_TIME,
};

export const starforceQuery = {
  queryKey: ['reference', 'starforce'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiGet<StarforceBundleDto>('/api/reference/starforce', signal),
  staleTime: REFERENCE_STALE_TIME,
};

export const pitchedItemsQuery = {
  queryKey: ['reference', 'pitched-items'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiGet<PitchedItemDto[]>('/api/reference/pitched-items', signal),
  staleTime: REFERENCE_STALE_TIME,
};

export function useWorlds() {
  return useQuery(worldsQuery);
}

export function useClasses() {
  return useQuery(classesQuery);
}

export function useReferenceTasks() {
  return useQuery(tasksQuery);
}

export function useBossCatalog() {
  return useQuery(bossesQuery);
}

export function useSymbolCatalog() {
  return useQuery(symbolsQuery);
}

export function useStarforceCatalog() {
  return useQuery(starforceQuery);
}

export function usePitchedItemCatalog() {
  return useQuery(pitchedItemsQuery);
}

/**
 * REV21: one class's HEXA node -> skill catalog. `HexaSection` still uses this for the uncataloged-
 * class "add/remove a flexible node" fallback flow (REV22 Fix 3 removed the Mastery skill-picker
 * dropdown this catalog used to drive — a Mastery Core's `skills` list now comes straight off
 * `HexaNodeDto` instead). `classSlug` is required by the endpoint, so the query stays disabled until one is available
 * (same `enabled` pattern as the per-character hooks in `characterProgress.ts`, not the other
 * no-param reference hooks above).
 */
export function useHexaClassSkills(classSlug: string | null) {
  return useQuery({
    queryKey: ['reference', 'hexa-class-skills', classSlug] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<HexaClassSkillDto[]>(`/api/reference/hexa-class-skills?classSlug=${encodeURIComponent(classSlug!)}`, signal),
    enabled: classSlug !== null && classSlug !== '',
    staleTime: REFERENCE_STALE_TIME,
  });
}

/** Heroic (Reboot) worlds first — the product's primary audience — then alphabetical. */
export function sortWorldsHeroicFirst(worlds: WorldDto[]): WorldDto[] {
  return [...worlds].sort((a, b) => {
    const heroicRank = (w: WorldDto) => (w.worldType === 'HEROIC' ? 0 : 1);
    const rankDiff = heroicRank(a) - heroicRank(b);
    return rankDiff !== 0 ? rankDiff : a.name.localeCompare(b.name);
  });
}

/**
 * REV18 (docs/specs/REV18-switch-classnames-toast-theme.md) — the class catalog's friendly display
 * name for a `classSlug` (e.g. `"dark-knight"` -> `"Dark Knight"`). Falls back to the raw slug when
 * the catalog hasn't loaded yet or the slug is unknown to it, so callers never render blank.
 */
export function classDisplayName(slug: string, classes: ClassDto[] | undefined): string {
  return classes?.find((k) => k.slug === slug)?.name ?? slug;
}

/** Classes grouped by branch (Explorer, Cygnus Knights, ...), each branch alphabetical by name. */
export function groupClassesByBranch(classes: ClassDto[]): Map<string, ClassDto[]> {
  const groups = new Map<string, ClassDto[]>();
  for (const klass of [...classes].sort((a, b) => a.name.localeCompare(b.name))) {
    const bucket = groups.get(klass.branch);
    if (bucket) {
      bucket.push(klass);
    } else {
      groups.set(klass.branch, [klass]);
    }
  }
  return groups;
}
